/**
 * Lightweight error logging utility for PubNub health check operations.
 *
 * This utility provides centralized error logging (console + Sentry) without
 * creating circular dependencies. It can be safely imported by grantToken.ts
 * and networkStatus.ts without causing dependency cycles.
 *
 * For consistency with the codebase's error handling, this utility:
 * - Logs errors to console.error
 * - Reports errors to Sentry via captureException
 * - Uses the same error reporting pattern as handleError
 *
 * @param error - The error to log and report
 * @param message - Optional message to include with the error
 * @param tags - Optional tags for Sentry context
 */
export function logHealthCheckError(
  error: unknown,
  message?: string,
  tags?: Record<string, unknown>,
): void {
  // Log to console for debugging
  console.error(message || 'Health check error:', error);

  // Report to Sentry for monitoring
  // Using dynamic import to avoid circular dependencies
  import('globals/sentry')
    .then(({ captureException }) => {
      return captureException(error, {
        tags: {
          errorBoundary: false,
          healthCheck: true,
          ...tags,
        },
      });
    })
    .catch((sentryError) => {
      // If Sentry fails to load, just log to console
      // This should rarely happen, but we don't want to break the app
      console.warn('Failed to report error to Sentry:', sentryError);
    });
}

